import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import documentConfigs from './documentConfigs.json';

// Import images
import edaplogo from "../img/edaplogo.png";
import bapSig from "../img/bap_sig.png";
import bapCache from "../img/Bap_Cache.png";
import papaSig from "../img/papa_sig.png";
import alomoSig from "../img/alomo_sig.png";
import rectorImage from "../img/rector.png";
import sepImage from "../img/sep.png";
import bacImage from "../img/bac.png";
import qrBackgroundImage from "../img/qrbac.png";

// Helper function to translate grades to English
const translateGradeToEnglish = (frenchGrade) => {
    const gradeMap = {
        "Très Honorable avec Félicitations du Jury": "Excellent with Jury Congratulations",
        "Très Honorable": "Excellent",
        "Honorable": "Very Good",
        "Bien": "Good",
        "Assez Bien": "Satisfactory"
    };
    return gradeMap[frenchGrade] || frenchGrade;
};

const DocumentTemplate = ({
    documentType,
    app,
    customStyles = {},
    onDataChange = null
}) => {
    // Initialize form data with default values
    const getInitialFormData = () => {
        const config = documentConfigs[documentType];
        if (!config) return {};

        const initialData = {};
        config.formFields.forEach(field => {
            if (field.defaultValue === 'currentDate') {
                initialData[field.name] = new Date().toISOString().split('T')[0];
            } else if (field.defaultValue) {
                initialData[field.name] = field.defaultValue;
            } else {
                initialData[field.name] = '';
            }
        });
        return initialData;
    };

    // All hooks must be called before any conditional returns
    const [formData, setFormData] = useState(getInitialFormData());
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const documentRef = useRef();
    const db = getDatabase(app);

    // Load documents from Firebase on component mount
    useEffect(() => {
        if (!documentType) return;

        const documentsRef = ref(db, `${documentType}/UNCR/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db, documentType]);

    // Handle ESC key press to close modal
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && modalVisible) {
                handleModalClose();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [modalVisible]);

    // Check config after hooks
    const config = documentConfigs[documentType];
    if (!config) {
        console.error(`Document type "${documentType}" not found in configuration`);
        return <div>Error: Document configuration not found</div>;
    }

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newFormData = {
            ...formData,
            [name]: value
        };
        setFormData(newFormData);

        // Call parent callback if provided
        if (onDataChange) {
            onDataChange(newFormData);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate required fields
        const requiredFields = config.formFields.filter(field => field.required);
        for (const field of requiredFields) {
            if (!formData[field.name] || formData[field.name].trim() === '') {
                alert(`${field.placeholder} est obligatoire!`);
                return;
            }
        }

        // Save to Firebase
        const documentRefPath = ref(db, `${documentType}/UNCR/${formData.matricule || Date.now()}`);
        set(documentRefPath, {
            ...formData,
            createdAt: new Date().toISOString()
        })
            .then(() => {
                console.log(`${documentType} saved successfully in Firebase`);
            })
            .catch((error) => {
                console.error(`Error saving ${documentType} to Firebase: `, error);
            });

        // Reset form
        setFormData(getInitialFormData());
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `${documentType}/UNCR/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
                // Refresh the documents list
                const documentsRef = ref(db, `${documentType}/UNCR/`);
                onValue(documentsRef, (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        const documentsArray = Object.keys(data).map(key => ({
                            id: key,
                            ...data[key]
                        }));
                        setDocuments(documentsArray);
                    } else {
                        setDocuments([]);
                    }
                });
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const downloadPDF = async (documentData) => {
        const element = document.getElementById(`${documentType}-${documentData.matricule || documentData.id}`);

        if (documentType === 'diploma' || documentType === 'diplome sans') {
            // Manual two-page PDF assembly to avoid blank middle page
            const tempContainer = document.createElement('div');
            tempContainer.style.cssText = 'position: fixed; top: -9999px; left: -9999px; z-index: -1;';

            const frenchPageId = `${documentType}-${documentData.matricule}-french`;
            const englishPageId = `${documentType}-${documentData.matricule}-english`;
            const frenchElement = document.getElementById(frenchPageId);
            const englishElement = document.getElementById(englishPageId);
            if (!frenchElement || !englishElement) {
                console.error('One or both diploma pages not found for PDF generation');
                return;
            }

            const frenchClone = frenchElement.cloneNode(true);
            const englishClone = englishElement.cloneNode(true);

            const normalizePage = (clone) => {
                clone.style.cssText += `
                    width: 210mm !important;
                    height: 297mm !important;
                    margin: 0 !important;
                    padding: 30mm !important; /* Match preview padding (3cm) */
                    background: white !important;
                    background-image: url(${bacImage}) !important;
                    background-size: 21.4cm 29.7cm !important;
                    background-repeat: no-repeat !important;
                    background-position: center !important;
                    box-sizing: border-box !important;
                    position: relative !important;
                `;
                clone.style.pageBreakAfter = 'auto';
                applyInlineStyles(clone, documentType, { preventCropping: true });
            };

            normalizePage(frenchClone);
            normalizePage(englishClone);

            tempContainer.appendChild(frenchClone);
            tempContainer.appendChild(englishClone);
            document.body.appendChild(tempContainer);

            const waitForImages = async (root) => {
                const imgs = [...root.querySelectorAll('img')];
                await Promise.all(imgs.map(img => {
                    if (img.complete && img.naturalWidth) return Promise.resolve();
                    return new Promise(res => {
                        img.onload = res; img.onerror = res;
                    });
                }));
            };

            await waitForImages(tempContainer);

            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const renderPageToPDF = async (node, isFirst) => {
                const canvas = await html2canvas(node, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#FFFFFF'
                });
                const imgData = canvas.toDataURL('image/png');
                if (!isFirst) pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
            };

            await renderPageToPDF(frenchClone, true);
            await renderPageToPDF(englishClone, false);

            pdf.save(`${documentData.noms?.replace(/\s+/g, '_') || 'document'}_${documentType}_bilingual.pdf`);
            document.body.removeChild(tempContainer);
        } else {
            // For other document types, use single page
            const clone = element.cloneNode(true);

            // Apply document-specific styles
            const styles = config.styles;
            clone.style.cssText = `
                width: ${styles.documentWidth} !important;
                min-height: ${styles.documentHeight} !important;
                margin: 0 auto !important;
                padding: ${styles.padding} !important;
                background-color: ${styles.backgroundColor} !important;
                ${styles.backgroundImage ? `background-image: url(${bacImage}) !important; background-size: 21.4cm 29.7cm !important; background-repeat: no-repeat !important; background-position: center !important;` : ''}
                font-family: ${styles.fontFamily} !important;
                line-height: 1.6 !important;
                position: relative !important;
                box-sizing: border-box !important;
            `;

            // Apply comprehensive inline styles for PDF generation
            applyInlineStyles(clone, documentType);

            // Create a temporary container
            const tempContainer = document.createElement('div');
            tempContainer.style.cssText = 'position: fixed; top: -9999px; left: -9999px; z-index: -1;';
            tempContainer.appendChild(clone);
            document.body.appendChild(tempContainer);

            const options = {
                margin: 0.5,
                filename: `${documentData.noms?.replace(/\s+/g, '_') || 'document'}_${documentType}.pdf`,
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: 'white',
                    width: clone.scrollWidth,
                    height: clone.scrollHeight
                },
                jsPDF: { format: 'a4', orientation: 'portrait' },
            };

            html2pdf().set(options).from(clone).save().then(() => {
                // Clean up
                document.body.removeChild(tempContainer);
            });
        }
    };

    // Render form fields
    const renderFormFields = () => {
        return config.formFields.map((field, index) => (
            <div key={field.name} className="document-form-group">
                {field.type === 'select' ? (
                    <select
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        required={field.required}
                    >
                        {field.options.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        placeholder={field.placeholder}
                        required={field.required}
                    />
                )}
            </div>
        ));
    };

    return (
        <div className="UNCR">
            <div className="document-form-container" style={customStyles.container}>
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo UNCR" className="document-form-logo" />
                    <h2>Formulaire de {documentType.charAt(0).toUpperCase() + documentType.slice(1)}</h2>
                </div>
                <form onSubmit={handleSubmit} className="document-form">
                    {renderFormFields()}
                    <div className="document-form-group full-width">
                        <button type="submit" className="document-generate-btn">
                            Créer le {documentType.charAt(0).toUpperCase() + documentType.slice(1)}
                        </button>
                    </div>
                </form>

                {/* List of saved documents */}
                <div className="document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="document-item">
                            <button
                                className="document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - {document.matricule}
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>

                {/* Modal for document preview */}
                <ConfigProvider
                    theme={{
                        token: {
                            // Prevent Ant Design from affecting global styles
                        }
                    }}
                >
                    <Modal
                        title={
                            <span style={{ color: 'white' }}>
                                {selectedDocument ? `Aperçu - ${selectedDocument.noms}` : `Aperçu du ${documentType}`}
                            </span>
                        }
                        open={modalVisible}
                        onCancel={handleModalClose}
                        width="95vw"
                        style={{ top: 10 }}
                        className="document-modal"
                        centered={false}
                        maskClosable={true}
                        keyboard={true}
                        footer={[
                            <Button
                                key="download"
                                type="primary"
                                icon={<Download size={16} />}
                                onClick={() => selectedDocument && downloadPDF(selectedDocument)}
                                style={{
                                    backgroundColor: '#28a745',
                                    borderColor: '#28a745',
                                    fontWeight: 'bold'
                                }}
                            >
                                Télécharger PDF
                            </Button>,
                            <Button
                                key="close"
                                onClick={handleModalClose}
                                style={{ fontWeight: 'bold' }}
                            >
                                Fermer
                            </Button>
                        ]}
                    >
                        {selectedDocument && (
                            <div className="document-modal-content">
                                <DocumentContent
                                    formData={selectedDocument}
                                    config={config}
                                    documentType={documentType}
                                />
                            </div>
                        )}
                    </Modal>
                </ConfigProvider>
            </div>
        </div>
    );
};

// Document Content Component
const DocumentContent = ({ formData, config, documentType }) => {
    // QR code shared data URL for both pages
    const [qrCodeData, setQrCodeData] = useState(null);

    // Helper to format date with month in letters (French or English)
    const formatDateWithMonthName = (dateStr, language = 'French') => {
        const date = new Date(dateStr);
        const monthsFrench = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const monthsEnglish = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const months = language === 'English' ? monthsEnglish : monthsFrench;
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    // Generate QR Code with form data
    useEffect(() => {
        const generateQRCode = async () => {
            let qrData = `${documentType.toUpperCase()} - UNCR\n`;
            Object.keys(formData).forEach(key => {
                if (key !== 'createdAt' && formData[key]) {
                    qrData += `${key}: ${formData[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: UNCR - Academie du Cercle de Reflexion des Nations`;
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (formData) generateQRCode();
    }, [formData, documentType]);

    // Get document title based on form data and language
    const getDocumentTitle = (language = 'French') => {
        if (typeof config.document.title === 'object') {
            const fullTitle = config.document.title[formData.diplomaType] ||
                config.document.title[Object.keys(config.document.title)[0]];

            // Split title for diploma to show ATTESTATION/CERTIFICATE on its own line
            if (documentType === 'diploma' || documentType === 'diplome sans') {
                // Extract the degree type (PhD) or (HDR) from the title
                const degreeMatch = fullTitle.match(/\((Ph\.D|HDR)\)/);
                const degreeType = degreeMatch ? degreeMatch[0] : '';

                if (language === 'English') {
                    return {
                        main: "CERTIFICATE", // H1 - Maximum bold
                        subtitle: "OF SUCCESS OF DOCTORAL STUDIES", // H2 - Regular bold  
                        degree: formData.diplomaType === 'PhD' ? '(PhD)' : '(HDR)' // H3 - Degree type
                    };
                } else {
                    const middleText = fullTitle.replace("ATTESTATION ", "").replace(degreeType, "").trim();
                    return {
                        main: "ATTESTATION", // H1 - Maximum bold
                        subtitle: middleText, // H2 - Regular bold  
                        degree: degreeType // H3 - Degree type on separate line
                    };
                }
            }
            return fullTitle;
        }
        return config.document.title;
    };

    // Render document body based on template
    const renderDocumentBody = (language = 'French') => {
        if (config.document.bodyTemplate === 'diplomaBody') {
            return renderDiplomaBody(language);
        } else if (config.document.bodyTemplate === 'attestationBody') {
            return renderAttestationBody();
        } else if (config.document.bodyTemplate === 'undDiplomaBody') {
            return renderUndDiplomaBody();
        }
        return <div>Body template not found</div>;
    };

    const renderDiplomaBody = (language = 'French') => {
        if (language === 'English') {
            return (
                <div className="document-body">
                    <p className="diploma-introduction">
                        In accordance with the partnership of joint PhD management between the Interuniversity Center of Multidisciplinary Research (CIREP), of the University of Lisala in DRC and IRGIB Africa University in Benin and The Cercle de Reflexion des Nations University in USA;
                    </p>
                    <p className="diploma-vu">
                        Considering the ministerial order n° 133/MINESU/CAB.MIN/NM/RS/2006 of April 25, 2006 on the creation and assumption of responsibility by the public treasury of a university center called "Lisala University Center";
                    </p>
                    <p className="diploma-vu">
                        In view of the decree no. 24/23 of March 15, 2024 relating to diplomas and academic degrees;
                    </p>
                    <p className="diploma-vu">
                        In view of the favorable reports of the deliberation commission, composed of CIREP and IRGIB Africa University and UNCR University;
                    </p>
                    <p className="diploma-main-text">
                        The Director General of CIREP certifies that {formData.genre === 'Femme' ? 'Ms.' : 'Mr.'} <strong>{formData.noms}</strong>, born on <strong>{formatDateWithMonthName(formData.dateNaissance, 'English')}</strong> at <strong>{formData.lieuNaissance}</strong>-<strong>{formData.paysNaissance}</strong> registered with No <strong>{formData.matricule}</strong>.
                    </p>
                    <p className="diploma-success-text">
                        Successfully passed the required tests and fulfilled all the conditions required by the regulations for the award of the Doctoral Certificate for "{formData.diplomaType}" in <strong>{formData.domaine}</strong>; Option: <strong>{formData.option}</strong>, obtained with Grade "<strong>{translateGradeToEnglish(formData.mention)}</strong>", academic year <strong>{formData.anneeAcademique}</strong> with all the rights and privileges it confers.
                    </p>
                    <p className="diploma-date" style={{ textAlign: 'center', marginTop: '40px' }}>
                        Done at Lisala on the <strong>{formatDateWithMonthName(formData.dateEmission, 'English')}</strong>
                    </p>
                </div>
            );
        } else {
            return (
                <div className="document-body">
                    <p className="diploma-introduction">
                        Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre International de Recherche Pluridisciplinaire (CIREP) de l'Université de Lisala en RDC, Université IRGIB AFRICA au Benin et Université du Cercle de Réflexion des Nations aux États-Unis;
                    </p>
                    <p className="diploma-vu">
                        Vu l'arrêté ministériel n° 133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d'un centre universitaire qualifié « Centre Universitaire de Lisala » ;
                    </p>
                    <p className="diploma-vu">
                        Vu le décret n° 24/23 du 15 Mars 2024 relatif aux diplômes et grades académiques;
                    </p>
                    <p className="diploma-vu">
                        Vu les rapports favorables de la commission de délibération, composée du CIREP ; Université IRGIB Africa et UNCR University;
                    </p>
                    <p className="diploma-main-text">
                        Nous attestons que <strong>{formData.noms}</strong>, né le <strong>{formatDateWithMonthName(formData.dateNaissance)}</strong> à <strong>{formData.lieuNaissance}</strong> au <strong>{formData.paysNaissance}</strong> enregistré sous No <strong>{formData.matricule}</strong>.
                    </p>
                    <p className="diploma-success-text">
                        A subi avec succès les épreuves requises et rempli toutes les conditions exigées par le règlement pour l'obtention du Diplôme de {formData.diplomaType === 'PhD' ? 'doctorat "Ph.D"' : 'postdoctorat "HDR"'} en <strong>{formData.domaine}</strong>, Option : <strong>{formData.option}</strong>, obtenu avec la mention <strong>"{formData.mention}"</strong>, année académique <strong>{formData.anneeAcademique}</strong> avec tous les droits et les privilèges qu'il confère.
                    </p>
                    <p className="diploma-date" style={{ textAlign: 'center', marginTop: '40px' }}>
                        Fait à Lisala, le <strong>{formatDateWithMonthName(formData.dateEmission)}</strong>
                    </p>
                </div>
            );
        }
    };

    const renderAttestationBody = () => {
        return (
            <div className="document-body">
                <p className="academic-year">Année Académique: {formData.anneeAcademique}</p>
                <p className="introduction">
                    Le Directeur Général de l'Académie du Cercle de Réflexion des Nations aux États Unis atteste par la présente que {formData.genre === 'Femme' ? 'la Chercheuse' : 'le Chercheur'} dont les détails suivants :
                </p>
                <div className="student-details">
                    <p>Noms d{formData.genre === 'Femme' ? 'e la Doctorante' : 'u Doctorant'}: <strong>{formData.noms}</strong></p>
                    <p>Matricule: <strong>{formData.matricule}</strong></p>
                    <p>Filière: <strong>{formData.filiere}</strong></p>
                    <p>Département: <strong>{formData.departement}</strong></p>
                    <p>Classe: <strong>{formData.classe}</strong></p>
                    <p>Laboratoire de rattachement : <strong>CIREP/Unité de recherche : {formData.laboratoire}</strong></p>
                </div>
                <p className="registration-text">
                    Est dûment {formData.genre === 'Femme' ? 'enregistrée' : 'enregistré'} en formation doctorale en cotutelle internationale de thèse et codiplomation entre UNCR University et {formData.universiteCodeplomation}
                </p>
            </div>
        );
    };

    const renderUndDiplomaBody = () => {
        return (
            <div className="document-body und-diploma-body">
                <div className="und-legal-references">
                    <p className="vu-clause">Vu la Loi-cadre n°14/004 du 11 février 2014 de l'Enseignement National;</p>
                    <p className="vu-clause">Vu le décret n° 22/39 du 8 décembre 2022 portant organisation et fonctionnement du système LMD en République Démocratique du Congo;</p>
                    <p className="vu-clause">Vu le décret n° 24/23. du 15 Mars 2024 relatif aux diplômes et grades académiques;</p>
                    <p className="vu-clause">Vu l'arrêté ministériel 401 du 28 août 2023 portant modalités d'évaluation, de progression et d'orientation dans les cycles d'études;</p>
                    <p className="vu-clause">Vu l'Arrêté Ministériel 0741/06/MINESU/CABMIN/SASM/MMK/2025 du 07 Juillet 2025 portant création et prise en charge par le Trésor Public de l'Université Nationale de la Décentralisation(UND) comme Etablissements de l'Enseignement Supérieur et Universitaire en RDC ;</p>
                    <p className="vu-clause">Vu l'Arrêté Ministériel 004 MINESU/CABMIN/SASM/MMK/2025 du 06 février 2025 portant organisation et fonctionnement de l'Enseignement ouvert et à distance au sein des Etablissements de l'Enseignement Supérieur et Universitaire en République Démocratique du Congo;</p>
                    <p className="vu-clause">Vu la collaboration entre l'UND et l'Académie de la Paix du Cercle de Réflexion des Nations pour décerner des co-diplômes avec UND;</p>
                    <p className="vu-clause">Vu les procès-verbaux du Jury attestant que l'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires ;</p>
                </div>
                
                <div className="und-diploma-declaration">
                    <p className="main-declaration">
                        Le diplôme de Doctorat en Sciences Juridiques Spécialité: <strong>{formData.specialite}</strong> est délivré à <strong>{formData.noms}</strong> né le <strong>{formatDateWithMonthName(formData.dateNaissance)}</strong> à <strong>{formData.lieuNaissance}</strong> Province du <strong>{formData.province}</strong>/<strong>{formData.pays}</strong> au titre de l'année académique <strong>{formData.anneeAcademique}</strong> avec la mention <strong>{formData.mention}</strong> et lui confère le grade de Doctorat (PhD), pour en jouir avec les droits et prérogatives qui y sont attachés.
                    </p>
                </div>

                <div className="und-emission-date">
                    <p>Fait à Kinshasa, le <strong>{formatDateWithMonthName(formData.dateEmission)}</strong></p>
                </div>
            </div>
        );
    };

    // Render a single page for a specific language
    const renderSinglePage = (language = 'French', pageId) => {
        const titleData = getDocumentTitle(language);

        return (
            <div
                className="document-document"
                id={pageId}
                style={{
                    backgroundImage: config.styles.backgroundImage ? `url(${bacImage})` : 'none',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    width: config.styles.documentWidth,
                    height: config.styles.documentHeight,
                    margin: '0 0 20px 0',
                    padding: config.styles.padding,
                    backgroundColor: 'white',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.2)',
                    fontFamily: "'Times New Roman', serif",
                    lineHeight: '1.6',
                    position: 'relative',
                    pageBreakAfter: 'always',
                    overflow: 'hidden'
                }}
            >
                <style>
                    {generateInlineStyles(config, documentType)}
                </style>

                {/* Header */}
                {config.document.hasHeader && (
                    <div className="document-header">
                        {documentType === 'und-diploma' ? (
                            <div className="und-header">
                                <div className="und-header-row">
                                    <div className="und-left-logo">
                                        <img src={edaplogo} alt="Logo Left" className="und-logo" />
                                    </div>
                                    <div className="und-header-center">
                                        <h1 className="und-country">{config.document.headerConfig.country}</h1>
                                        <h2 className="und-university">{config.document.headerConfig.university}</h2>
                                        <p className="und-address">{config.document.headerConfig.address}</p>
                                        <p className="und-contact">{config.document.headerConfig.contact}</p>
                                    </div>
                                    <div className="und-right-logo">
                                        <img src={edaplogo} alt="Logo Right" className="und-logo" />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="header-text">
                                <h2 className="academy-name">{config.document.headerConfig.academyName}</h2>
                                <div className="logo-container">
                                    <img src={edaplogo} alt="Logo UNCR" className="logo" />
                                </div>
                                <h3 className="university-name">{config.document.headerConfig.universityName}</h3>
                                <p className="address"><strong>{config.document.headerConfig.address}</strong></p>
                                <p className="contact"><strong>{config.document.headerConfig.contact}</strong></p>
                            </div>
                        )}
                    </div>
                )}

                {/* UND Diploma Title Section (separate from header) */}
                {documentType === 'und-diploma' && (
                    <div className="und-title-section">
                        <div className="und-title-row">
                            <div className="und-title-spacer"></div>
                            <h1 className="und-diploma-title">{titleData}</h1>
                            <div className="und-number-box">
                                <div className="number-label">N°</div>
                                <div className="number-value">{formData.numeroDoc || ''}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Document Content */}
                <div className="document-content" style={{
                    marginTop: documentType === 'und-diploma' ? '0px' : (documentType === 'diploma' || documentType === 'diplome sans') ? '-100px' : '-20px',
                    padding: '0',
                    minHeight: 'calc(100% - 120px)'
                }}>
                    {documentType === 'und-diploma' ? (
                        <div className="und-content-body">
                            {/* Content will be rendered by renderDocumentBody */}
                        </div>
                    ) : (documentType === 'diploma' || documentType === 'diplome sans') ? (
                        <div className="diploma-title">
                            {/* CERTIFICATE/ATTESTATION - H1 Maximum Bold */}
                            <h1 className="title-main">{titleData.main}</h1>
                            {/* Middle text - H2 Regular Bold */}
                            <h2 className="title-subtitle">{titleData.subtitle}</h2>
                            {/* Degree type - H3 on separate line */}
                            <h3 className="title-degree">{titleData.degree}</h3>
                        </div>
                    ) : (
                        <h1 className="title">{titleData}</h1>
                    )}

                    {/* Separator - moved after title */}
                    {config.document.hasSeparator && (
                        config.document.separatorType === 'line' ? (
                            <div className="separator"></div>
                        ) : (
                            <div className="separator-image">
                                <img src={sepImage} alt="Separator" />
                            </div>
                        )
                    )}

                    {/* Document Body */}
                    {renderDocumentBody(language)}

                    {/* Signatures */}
                    {config.document.hasSignatures && (
                        <div className="signature-section">
                            {config.document.signatureConfig.type === 'single' ? (
                                <div className="director-signature">
                                    <div className="signatures">
                                        <div className="signature-text">
                                            <p className="director-title">{config.document.signatureConfig.left.title}</p>
                                            <p className="director-name">{config.document.signatureConfig.left.name}</p>
                                        </div>
                                        <div className="signature-images">
                                            <img src={bapSig} alt="Signature" className="signature-img" />
                                            <img src={bapCache} alt="Cachet" className="stamp-img" />
                                        </div>
                                    </div>
                                </div>
                            ) : documentType === 'und-diploma' ? (
                                <div className="und-signatures">
                                    <div className="und-signature-row">
                                        {/* Left Signature */}
                                        <div className="und-left-signature">
                                            <hr className="signature-line-separator" />
                                            <p className="signature-title">{config.document.signatureConfig.left.title}</p>
                                        </div>
                                        {/* Right Signature */}
                                        <div className="und-right-signature">
                                            <hr className="signature-line-separator" />
                                            <p className="signature-title">{config.document.signatureConfig.right.title}</p>
                                        </div>
                                        {/* QR Code - positioned to the right */}
                                        {config.document.hasQRCode && (
                                            <div className="und-qr-section">
                                                <img
                                                    src={qrCodeData || ''}
                                                    alt="QR Code - Document Verification"
                                                    className="qr-code"
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        border: '1px solid #000'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="dual-signatures">
                                    {/* Signatures Row - Better alignment with margins */}
                                    <div className="signature-row" style={{ marginTop: '60px' }}>
                                        {/* Left Signature */}
                                        <div className={`left-signature ${documentType === 'diplome sans' ? 'left-signature-sans' : 'left-signature-default'}`}>
                                            {/* Signature Image - Fixed 150px width */}
                                            <img src={papaSig} alt="Left Signature" className="signature-img"
                                                style={{ width: '150px', height: 'auto' }} />
                                            <hr className="signature-line-separator" />
                                            <p className="signature-title">
                                                {language === 'English' ? 'Director of the Doctoral School' : 'Directeur de l\'École Doctorale'}
                                            </p>
                                            <p className="signature-name">Pr. NZEYIMANA Isaie</p>
                                        </div>
                                        {/* QR Code Section - Center positioning */}
                                        <div className="center-qr">
                                            {config.document.hasQRCode && (
                                                <div className="qr-container-center" style={{
                                                    backgroundImage: `url(${qrBackgroundImage})`,
                                                    backgroundSize: 'contain',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'center',
                                                    backgroundColor: 'transparent'
                                                }}>
                                                    {/* QR Code - Smaller size to fit inside background */}
                                                    <img
                                                        src={qrCodeData || ''}
                                                        alt={`QR Code - Document Verification (${language})`}
                                                        className="qr-code"
                                                        style={{
                                                            width: '48px',
                                                            height: '48px'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {/* Right Signature */}
                                        <div
                                            className={`right-signature ${documentType === 'diplome sans' ? 'right-signature-sans right-signature--sans' : 'right-signature-default right-signature--default'}`}
                                            style={{ width: '200px' }}
                                        >
                                            {/* Signature Image - Fixed 150px width (conditionally hidden for "diplome sans") */}
                                            {!config.document.signatureConfig.right?.hideImage && (
                                                <img src={alomoSig} alt="Right Signature" className="signature-img" style={{ width: '150px', height: 'auto' }} />
                                            )}
                                            {/* Rector Image - Only for "diplome sans" */}
                                            {documentType === 'diplome sans' && (
                                                <img src={rectorImage} alt="Rector" style={{ width: '150px', height: 'auto', marginTop: '-45px', marginBottom: '10px' }} />
                                            )}
                                            <hr className="signature-line-separator" />
                                            <p className="signature-title" style={{ fontSize: '11px' }}>
                                                {language === 'English' ? 'Rector of Université de Lisala' : 'Recteur de l\'Université de Lisala'}
                                            </p>
                                            <p className="signature-name">Pr Abbé Donat TEBAKABE ALOMO</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {config.document.footer && (
                    <div className="document-footer" style={{
                        padding: (documentType === 'diploma' || documentType === 'diplome sans') ? '0 40px' : '0',
                        bottom: (documentType === 'diploma' || documentType === 'diplome sans') ? '60px' : '20px'
                    }}>
                        <span className="footer-left">{config.document.footer.left}</span>
                        <span className="footer-center">{config.document.footer.center.replace('{matricule}', formData.matricule)}</span>
                        <span className="footer-right">{config.document.footer.right}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='UNCR'>
            {/* Create bilingual diploma with two pages for diploma/diplome sans, single page for attestation */}
            {(documentType === 'diploma' || documentType === 'diplome sans') ? (
                <div className="bilingual-document-container" id={`${documentType}-${formData.matricule}`}>
                    {/* French Version - Page 1 */}
                    {renderSinglePage('French', `${documentType}-${formData.matricule}-french`)}

                    {/* English Version - Page 2 */}
                    {renderSinglePage('English', `${documentType}-${formData.matricule}-english`)}
                </div>
            ) : (
                <div className="document-container" id={`${documentType}-${formData.matricule}`}>
                    {renderSinglePage('French', `${documentType}-${formData.matricule}`)}
                </div>
            )}
        </div>
    );
};

// Generate inline styles for PDF
const generateInlineStyles = (config, documentType) => {
    const styles = config.styles;
    return `
        .document-document {
            width: ${styles.documentWidth} !important;
            min-height: ${styles.documentHeight} !important;
            margin: 0 auto !important;
            padding: ${styles.padding} !important;
            background-color: ${styles.backgroundColor} !important;
            font-family: ${styles.fontFamily} !important;
            line-height: 1.6 !important;
            position: relative !important;
            box-sizing: border-box !important;
            ${styles.backgroundImage ? `
                background-image: url(${bacImage}) !important;
                background-size: 21.4cm 29.7cm  !important;
                background-repeat: no-repeat !important;
                background-position: center !important;
            ` : ''}
        }
        
        ${(documentType === 'diploma' || documentType === 'diplome sans') ? `
            .document-content {
                margin-top: -100px !important;
                padding: 0 !important;
                position: relative !important;
                z-index: 1 !important;
                text-align: left !important;
                height: calc(100% - 120px) !important;
                overflow: hidden !important;
            }
            .document-body {
                font-size: 13px !important;
                line-height: 1.6 !important;
                text-align: justify !important;
                margin-bottom: 20px !important;
            }
            .diploma-introduction, .diploma-vu, .diploma-main-text, .diploma-success-text {
                margin: 12px 0 !important;
                text-align: justify !important;
                font-size: 13px !important;
                line-height: 1.6 !important;
            }
            .signature-row {
                margin-top: 30px !important;
                padding: 0 15px !important;
            }
            .document-footer {
                position: absolute !important;
                bottom: 40px !important;
                left: 40px !important;
                right: 40px !important;
                font-size: 11px !important;
            }
            
            .title {
                text-align: center !important;
                font-size: 24px !important;
                font-weight: bold !important;
                color: #000000 !important;
                margin: 30px 0 !important;
                text-transform: uppercase !important;
            }
            .diploma-title {
                text-align: center !important;
                margin: 30px 0 !important;
            }
            .title-main {
                font-size: 28px !important;
                font-weight: bolder !important;
                color: #000000 !important;
                margin: 0 0 10px 0 !important;
                text-transform: uppercase !important;
            }
            .title-subtitle {
                font-size: 20px !important;
                font-weight: bold !important;
                color: #000000 !important;
                margin: 0 !important;
                text-transform: uppercase !important;
            }
            .title-degree {
                font-size: 16px !important;
                font-weight: bold !important;
                color: #000000 !important;
                margin: 10px 0 0 0 !important;
                text-transform: uppercase !important;
            }
            .separator-image {
                text-align: center !important;
                margin: -25px 0 10px !important;
            }
            .separator-image img {
                max-width: 100% !important;
                height: auto !important;
            }
            .dual-signatures {
                margin-top: 50px !important;
            }
            .signature-separator {
                border: none !important;
                border-top: 2px solid #000000 !important;
                margin: 20px 0 !important;
            }
            .signature-row {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-start !important;
                margin-top: 30px !important;
                padding: 0 20px !important;
            }
            .left-signature, .right-signature {
                text-align: center !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
            }
            .right-signature {
                width: 200px !important;
                margin-top: 20px !important;
            }
            .center-qr {
                flex: 1 !important;
                display: flex !important;
                justify-content: center !important;
                align-items: flex-start !important;
                max-width: 40% !important;
            }
            .qr-container-center {
                text-align: center !important;
                background-color: transparent !important;
                padding: 21px !important;
                border: none !important;
                border-radius: 10px !important;
            }
            .signature-line-separator {
                border: none !important;
                border-top: 1px solid #000000 !important;
                width: 80% !important;
                margin: 10px 0 !important;
            }
            .signature-title {
                font-weight: bold !important;
                font-size: 11px !important;
                margin: 8px 0 5px 0 !important;
                text-align: center !important;
                color: #000000 !important;
            }
            .signature-name {
                font-weight: bold !important;
                font-size: 14px !important;
                margin: 5px 0 !important;
                text-align: center !important;
                color: #000000 !important;
            }
            .qr-container.bottom-center {
                position: absolute !important;
                bottom: 60px !important;
                left: 50% !important;
                transform: translateX(-50%) !important;
                text-align: center !important;
            }
            .document-footer {
                position: absolute !important;
                bottom: 60px !important;
                left: 0 !important;
                right: 0 !important;
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                font-size: 12px !important;
                font-weight: bold !important;
                color: #000 !important;
                padding: 0 40px !important;
            }
        ` : ''}
        
        ${documentType === 'und-diploma' ? `
            /* UND Diploma Landscape Styles */
            .document-document {
                width: 29.7cm !important;
                height: 21cm !important;
                padding: 2cm !important;
            }
            .und-header {
                margin-bottom: 30px !important;
            }
            .und-header-row {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-start !important;
                margin-bottom: 20px !important;
            }
            .und-left-logo, .und-right-logo {
                flex: 0 0 80px !important;
            }
            .und-logo {
                width: 60px !important;
                height: auto !important;
            }
            .und-header-center {
                flex: 1 !important;
                text-align: center !important;
                padding: 0 20px !important;
            }
            .und-country {
                font-size: 18px !important;
                font-weight: bold !important;
                margin: 0 0 5px 0 !important;
                color: #000 !important;
            }
            .und-university {
                font-size: 16px !important;
                font-weight: bold !important;
                margin: 0 0 10px 0 !important;
                color: #000 !important;
            }
            .und-address, .und-contact {
                font-size: 11px !important;
                margin: 2px 0 !important;
                color: #000 !important;
            }
            .und-title-section {
                margin: 30px 0 !important;
                padding: 0 2cm !important;
            }
            .und-title-row {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                margin-bottom: 30px !important;
            }
            .und-title-spacer {
                flex: 0 0 150px !important;
            }
            .und-diploma-title {
                font-size: 24px !important;
                font-weight: bold !important;
                color: #000 !important;
                margin: 0 !important;
                text-transform: uppercase !important;
                flex: 1 !important;
                text-align: center !important;
            }
            .und-number-box {
                border: 1px solid #000 !important;
                width: 150px !important;
                height: 40px !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: center !important;
                align-items: flex-start !important;
                padding-left: 10px !important;
                flex: 0 0 150px !important;
            }
            .number-label {
                font-size: 14px !important;
                font-weight: bold !important;
                margin-bottom: 2px !important;
            }
            .number-value {
                font-size: 12px !important;
                height: 15px !important;
            }
            .und-diploma-body {
                font-size: 13px !important;
                line-height: 1.5 !important;
                text-align: justify !important;
            }
            .und-legal-references {
                margin-bottom: 25px !important;
            }
            .vu-clause {
                margin: 8px 0 !important;
                text-align: justify !important;
                font-size: 12px !important;
                line-height: 1.4 !important;
            }
            .und-diploma-declaration {
                margin: 25px 0 !important;
            }
            .main-declaration {
                font-size: 14px !important;
                line-height: 1.6 !important;
                text-align: justify !important;
                font-weight: 500 !important;
            }
            .und-emission-date {
                text-align: center !important;
                margin: 30px 0 20px 0 !important;
            }
            .und-signatures {
                margin-top: 40px !important;
            }
            .und-signature-row {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-end !important;
                padding: 0 50px !important;
            }
            .und-left-signature, .und-right-signature {
                flex: 1 !important;
                text-align: center !important;
                margin: 0 30px !important;
            }
            .und-qr-section {
                flex: 0 0 80px !important;
                text-align: right !important;
            }
        ` : ''}
        
        .document-header {
            text-align: center !important;
            margin-bottom: 30px !important;
        }
        .academy-name {
            color: #28a745 !important;
            font-size: 20px !important;
            font-weight: bold !important;
            margin: 0 0 10px 0 !important;
        }
        .university-name {
            color: #28a745 !important;
            font-size: 18px !important;
            font-weight: bold !important;
            margin: 10px 0 !important;
        }
        .address, .contact {
            font-size: 14px !important;
            color: #333333 !important;
            margin: 5px 0 !important;
        }
        .separator {
            height: 2px !important;
            background-color: #000000 !important;
            margin: 20px 0 !important;
            border: none !important;
        }
        .document-body {
            font-size: 14px !important;
            line-height: 1.8 !important;
            color: #000000 !important;
        }
        .qr-container {
            background-color: white !important;
            padding: 10px !important;
            border: 1px solid #ddd !important;
            border-radius: 8px !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        }
        .qr-container.bottom-right {
            position: absolute !important;
            bottom: 2cm !important;
            right: 2cm !important;
            text-align: center !important;
        }
        .document-footer {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            font-size: 12px !important;
            font-weight: bold !important;
            color: #000000 !important;
        }
        .footer-left {
            flex: 1 !important;
            text-align: left !important;
        }
        .footer-center {
            flex: 1 !important;
            text-align: center !important;
        }
        .footer-right {
            flex: 1 !important;
            text-align: right !important;
        }
    `;
};

// Apply inline styles for PDF generation
const applyInlineStyles = (element, documentType, options = {}) => {
    // First, apply the background image to the main document
    const documentElement = element.querySelector('.document-document');
    if (documentElement) {
        documentElement.style.cssText += `
            background-image: url(${bacImage}) !important;
            background-size: 21.4cm 29.7cm !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
        `;
    }

    // Apply QR background images to containers
    const qrContainers = element.querySelectorAll('.qr-container-center');
    qrContainers.forEach(container => {
        container.style.cssText += `
            background-image: url(${qrBackgroundImage}) !important;
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
            background-color: transparent !important;
            padding: 21px !important;
            border: none !important;
        `;
    });

    if (documentType === 'und-diploma') {
        // UND Diploma landscape-specific inline styles for PDF
        const undStyles = {
            '.document-document': 'width: 29.7cm !important; height: 21cm !important; padding: 2cm !important; background: white !important; position: relative !important;',
            '.und-header-row': 'display: flex !important; justify-content: space-between !important; align-items: flex-start !important; margin-bottom: 20px !important;',
            '.und-left-logo, .und-right-logo': 'flex: 0 0 80px !important;',
            '.und-logo': 'width: 60px !important; height: auto !important;',
            '.und-header-center': 'flex: 1 !important; text-align: center !important; padding: 0 20px !important;',
            '.und-country': 'font-size: 18px !important; font-weight: bold !important; margin: 0 0 5px 0 !important; color: #000 !important;',
            '.und-university': 'font-size: 16px !important; font-weight: bold !important; margin: 0 0 10px 0 !important; color: #000 !important;',
            '.und-address, .und-contact': 'font-size: 11px !important; margin: 2px 0 !important; color: #000 !important;',
            '.und-title-section': 'margin: 30px 0 !important; padding: 0 2cm !important;',
            '.und-title-row': 'display: flex !important; justify-content: space-between !important; align-items: center !important; margin-bottom: 30px !important;',
            '.und-title-spacer': 'flex: 0 0 150px !important;',
            '.und-diploma-title': 'font-size: 24px !important; font-weight: bold !important; color: #000 !important; margin: 0 !important; text-transform: uppercase !important; flex: 1 !important; text-align: center !important;',
            '.und-number-box': 'border: 1px solid #000 !important; width: 150px !important; height: 40px !important; display: flex !important; flex-direction: column !important; justify-content: center !important; align-items: flex-start !important; padding-left: 10px !important; flex: 0 0 150px !important;',
            '.number-label': 'font-size: 14px !important; font-weight: bold !important; margin-bottom: 2px !important;',
            '.number-value': 'font-size: 12px !important; height: 15px !important;',
            '.und-diploma-body': 'font-size: 13px !important; line-height: 1.5 !important; text-align: justify !important;',
            '.vu-clause': 'margin: 8px 0 !important; text-align: justify !important; font-size: 12px !important; line-height: 1.4 !important;',
            '.main-declaration': 'font-size: 14px !important; line-height: 1.6 !important; text-align: justify !important; font-weight: 500 !important; margin: 25px 0 !important;',
            '.und-emission-date': 'text-align: center !important; margin: 30px 0 20px 0 !important;',
            '.und-signature-row': 'display: flex !important; justify-content: space-between !important; align-items: flex-end !important; padding: 0 50px !important; margin-top: 40px !important;',
            '.und-left-signature, .und-right-signature': 'flex: 1 !important; text-align: center !important; margin: 0 30px !important;',
            '.und-qr-section': 'flex: 0 0 80px !important; text-align: right !important;',
            '.signature-line-separator': 'border: none !important; border-top: 1px solid #000000 !important; width: 80% !important; margin: 10px auto !important;',
            '.signature-title': 'font-weight: bold !important; font-size: 12px !important; margin: 8px 0 5px 0 !important; text-align: center !important; color: #000000 !important;'
        };

        // Apply UND styles
        Object.keys(undStyles).forEach(selector => {
            const elements = element.querySelectorAll(selector);
            elements.forEach(elem => {
                if (elem) {
                    elem.style.cssText += undStyles[selector];
                }
            });
        });

    } else if (documentType === 'diploma' || documentType === 'diplome sans') {
        // Diploma-specific inline styles for PDF
        const diplomaStyles = {
            // Title styles - Match the actual HTML structure
            '.diploma-title': 'text-align: center !important; margin: 30px 0 !important;',
            '.title-main': 'font-size: 28px !important; font-weight: 900 !important; color: #000000 !important; margin: 0 0 10px 0 !important; text-transform: uppercase !important;',
            '.title-subtitle': 'font-size: 20px !important; font-weight: bold !important; color: #000000 !important; margin: 0 !important; text-transform: uppercase !important;',
            '.title-degree': 'font-size: 16px !important; font-weight: bold !important; color: #000000 !important; margin: 10px 0 0 0 !important; text-transform: uppercase !important;',

            // Document structure
            '.document-content': 'margin-top: -100px !important; padding: 0 !important; position: relative !important; z-index: 1 !important; text-align: left !important;' + (options.preventCropping ? ' overflow: visible !important;' : ''),
            '.document-body': 'font-size: 14px !important; line-height: 1.8 !important; text-align: justify !important; overflow: visible !important;',

            // Separator
            '.separator-image': 'text-align: center !important; margin: -25px 0 10px !important;',
            '.separator-image img': 'max-width: 100% !important; height: auto !important;',

            // Document body paragraphs
            '.diploma-introduction, .diploma-vu, .diploma-main-text, .diploma-success-text': 'margin: 12px 0 !important; text-align: justify !important; font-size: 13px !important; line-height: 1.6 !important; color: #000000 !important;',
            '.diploma-date': 'text-align: center !important; margin-top: 30px !important; font-size: 13px !important; color: #000000 !important;',

            // Signatures
            '.signature-row': 'display: flex !important; justify-content: space-between !important; align-items: flex-start !important; margin-top: 40px !important; padding: 0 20px !important;',
            '.center-qr': 'flex: 1 !important; display: flex !important; justify-content: center !important; align-items: flex-start !important; max-width: 40% !important;',

            // Signature components
            '.signature-line-separator': 'border: none !important; border-top: 1px solid #000000 !important; width: 80% !important; margin: 10px 0 !important;',
            '.signature-title': 'font-weight: bold !important; font-size: 11px !important; margin: 8px 0 5px 0 !important; text-align: center !important; color: #000000 !important;',
            '.signature-name': 'font-weight: bold !important; font-size: 14px !important; margin: 5px 0 !important; text-align: center !important; color: #000000 !important;',
            '.signature-img': 'width: 200px !important; height: auto !important;',

            // QR Code
            '.qr-container-center': `text-align: center !important; background-image: url(${qrBackgroundImage}) !important; background-size: cover !important; background-position: center !important; background-repeat: no-repeat !important; background-color: transparent !important; padding: 25px !important; border: none !important; border-radius: 10px !important; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;`,
            '.qr-container.bottom-center': `position: absolute !important; bottom: 80px !important; left: 50% !important; transform: translateX(-50%) !important; text-align: center !important; background-image: url(${qrBackgroundImage}) !important; background-size: cover !important; background-position: center !important; background-repeat: no-repeat !important; background-color: transparent !important; padding: 15px !important; border: 2px solid #000000 !important; border-radius: 10px !important;`,
            '.qr-code': 'transform: translateY(-5px) !important; width: 43px !important; height: 48px !important; border: 1px solid black !important; border-radius: 6px !important; box-shadow: none !important;',

            // Footer
            '.document-footer': 'position: absolute !important; bottom: 60px !important; left: 3cm !important; right: 3cm !important; display: flex !important; justify-content: space-between !important; align-items: center !important; font-size: 12px !important; font-weight: bold !important; color: #000000 !important; padding: 0 40px !important;',
            '.footer-left': 'flex: 1 !important; text-align: left !important;',
            '.footer-center': 'flex: 1 !important; text-align: center !important;',
            '.footer-right': 'flex: 1 !important; text-align: right !important;',
        };

        // Separate signature styles for diploma vs diplome sans using unique class names
        const signatureVariantStyles = documentType === 'diplome sans'
            ? {
                '.left-signature-sans': 'width: 200px !important; text-align: center !important; display: flex !important; flex-direction: column !important; align-items: center !important; margin: 0 30px 0 -20px !important; padding-right: 20px !important;',
                '.right-signature-sans': 'width: 250px !important; text-align: center !important; display: flex !important; flex-direction: column !important; align-items: center !important; margin: 45px -25px 0 10px !important;'
            }
            : {
                '.left-signature-default': 'width: 200px !important; text-align: center !important; display: flex !important; flex-direction: column !important; align-items: center !important; margin: 0 -10px !important;',
                '.right-signature-default': 'width: 250px !important; text-align: center !important; display: flex !important; flex-direction: column !important; align-items: center !important; margin: -40px !important;'
            };

        Object.assign(diplomaStyles, signatureVariantStyles);

        // Apply the styles
        Object.keys(diplomaStyles).forEach(selector => {
            const elements = element.querySelectorAll(selector);
            elements.forEach(elem => {
                if (elem) {
                    elem.style.cssText += diplomaStyles[selector];
                }
            });
        });

        // CRITICAL FIX: Apply image styles directly by element type
        const allImages = element.querySelectorAll('img');
        allImages.forEach(img => {
            if (img.alt && img.alt.includes('Signature')) {
                img.style.cssText = 'width: 150px !important; height: auto !important; display: block !important; margin: 0 auto 10px auto !important;';
            } else if (img.alt && img.alt.includes('QR Code')) {
                img.style.cssText = 'width: 43px !important; height: 48px !important; border: 1px solid black !important; border-radius: 6px !important; box-shadow: none !important; transform: translateY(-5px) !important;';
            } else if (img.alt && img.alt.includes('Separator')) {
                img.style.cssText = 'max-width: 100% !important; height: auto !important;';
            }
        });

    } else {
        // Attestation styles (existing code)
        const defaultStyles = {
            '.title': 'text-align: center !important; font-size: 24px !important; font-weight: bold !important; margin: 30px 0 !important; color: #000000 !important;',
            '.document-body': 'font-size: 14px !important; line-height: 1.8 !important; color: #000000 !important;',
            '.separator': 'height: 2px !important; background-color: #000000 !important; margin: 20px 0 !important;',
            '.qr-container': 'background-color: white !important; padding: 10px !important; border: 1px solid #ddd !important;',
            '.qr-code': 'width: 48px !important; height: 48px !important; border: 2px solid #000000 !important;'
        };

        Object.keys(defaultStyles).forEach(selector => {
            const elements = element.querySelectorAll(selector);
            elements.forEach(elem => {
                if (elem) {
                    elem.style.cssText += defaultStyles[selector];
                }
            });
        });

        // Apply direct image styling for attestation
        const images = element.querySelectorAll('img');
        images.forEach(img => {
            if (img.alt && img.alt.includes('Signature')) {
                img.style.cssText = 'width: 150px !important; height: auto !important; display: block !important; margin: 0 auto !important;';
            }
        });
    }
};

export default DocumentTemplate;
